/**
 * POP Authority project statuses and helpers (mirror of web/helpers/project_statuses.py,
 * POP Authority section). The payload shape is the White Glove one plus `next_line`, `meta`
 * and `timeline`; see `getWhiteGloveSiteCardInsight` in whiteGloveSiteList.js for the shared part.
 */
import { auditHasCompletedOnboarding, ordersForSite } from '@/utils/whiteGloveSiteList'

export const POP_AUTHORITY_PROJECT_STATUS = {
  PROJECT_CREATED: 'project_created',
  SETUP_ORDERED: 'setup_ordered',
  SETUP_PUB_PENDING: 'setup_pub_pending',
  SETUP_GSC_PENDING: 'setup_gsc_pending',
  SETUP_IN_PROGRESS: 'setup_in_progress',
  CALL_SCHEDULED: 'call_scheduled',
  AUDIT_DELIVERED: 'audit_delivered',
  SUBSCRIPTION_PENDING: 'subscription_pending',
  SUBSCRIPTION_INVOICE_SENT: 'subscription_invoice_sent',
  SUBSCRIPTION_ACTIVE: 'subscription_active',
  SUBSCRIPTION_CANCELED: 'canceled',
  PLAN_CONTENT_ORDERED: 'plan_content_ordered',
  PLAN_CONTENT_DELIVERED: 'plan_content_delivered',
  PLAN_CONTENT_CHANGES_REQUESTED: 'plan_content_changes_requested',
  PLAN_CONTENT_REVISED: 'plan_content_revised',
  PLAN_CONTENT_APPROVED: 'plan_content_approved',
  PLAN_CONTENT_AUTO_APPROVED: 'plan_content_auto_approved',
  PLAN_CONTENT_PUBLISHING_ACCESS_NEEDED: 'plan_content_publishing_access_needed',
  PLAN_CONTENT_PUBLISHING_IN_PROGRESS: 'plan_content_publishing_in_progress',
  PLAN_CONTENT_PUBLISHED: 'plan_content_published',
  PLAN_CONTENT_AWAITING_URLS: 'plan_content_awaiting_urls',
  MONTH_N_STARTED: 'month_n_started',
  CLOSED: 'closed',
  BILLING_FAILED: 'billing_failed',
  PAUSED: 'paused',
  // the free audit created once every content plan of a live package is produced
  NEXT_AUDIT_IN_PROGRESS: 'next_audit_in_progress',
  NEXT_AUDIT_DELIVERED: 'next_audit_delivered',
}

/** While a site is in one of these the site page shows the setup wizard instead of the tabs. */
export const POP_AUTHORITY_SETUP_STATUS_KEYS = [
  POP_AUTHORITY_PROJECT_STATUS.PROJECT_CREATED,
  POP_AUTHORITY_PROJECT_STATUS.SETUP_ORDERED,
  POP_AUTHORITY_PROJECT_STATUS.SETUP_PUB_PENDING,
  POP_AUTHORITY_PROJECT_STATUS.SETUP_GSC_PENDING,
]

/** Audit package that pays for a POP Authority project setup (models.AVAILABLE_WHITE_GLOVE_ADDON_PACKAGES). */
export const POP_AUTHORITY_SETUP_FEE_PACKAGE_KEY = 'POP_AUTHORITY_SETUP_FEE'

/** Audit package of the free next content audit (models.POP_AUTHORITY_NEXT_AUDIT_PACKAGE_KEY). */
export const POP_AUTHORITY_NEXT_AUDIT_PACKAGE_KEY = 'POP_AUTHORITY_NEXT_AUDIT'

/** True for the audit created automatically, free of charge, once the content plans are all produced. */
export function popAuthorityIsNextAudit (audit) {
  return Boolean(audit && (audit.packageKey || audit.package_key) === POP_AUTHORITY_NEXT_AUDIT_PACKAGE_KEY)
}

/**
 * Package catalogue mirror of web/helpers/pop_authority.py (silos per month = content plans per
 * month, each 1 top-level + `supporting_per_silo` supporting keywords). The effective package of a
 * site comes from the status payload (`package`, provisional Growth until Phase 5).
 */
export const POP_AUTHORITY_PACKAGES = [
  {
    key: 'starter',
    name: 'Authority Starter',
    price: 555,
    silos_per_month: 1,
    supporting_per_silo: 3,
    consult_hours: 3,
    publishing_hours: 4,
    tagline: 'For businesses getting started on their growth trajectory.',
  },
  {
    key: 'growth',
    name: 'Authority Growth',
    price: 799,
    silos_per_month: 2,
    supporting_per_silo: 3,
    consult_hours: 3.5,
    publishing_hours: 5,
    tagline: 'For businesses ready to accelerate and dominate their niche.',
  },
  {
    key: 'pro',
    name: 'Authority Pro',
    price: 1047,
    silos_per_month: 2,
    supporting_per_silo: 5,
    consult_hours: 4,
    publishing_hours: 10,
    tagline: 'For businesses with serious ambition and competitive markets.',
  },
]
export const POP_AUTHORITY_CUSTOM_TAGLINE = 'Build your own mix of pages to match your goals and budget.'
/** Public terms of the 90-day growth guarantee (design PackageChooser "(see terms)"). */
export const POP_AUTHORITY_GUARANTEE_TERMS_URL = 'https://www.pageoptimizer.pro/pop-authority-90-day-visibility-guarantee'

/** "3.5 hrs" for the card bullets ("Up to 3.5 hrs consultation & campaign management"). */
export function popAuthorityHoursLabel (hours) {
  const n = Number(hours)
  if (!Number.isFinite(n) || n <= 0) return ''
  return `${Number.isInteger(n) ? n : n.toFixed(1).replace(/\.0$/, '')} hrs`
}
export const POP_AUTHORITY_DEFAULT_PACKAGE_KEY = 'growth'
export const POP_AUTHORITY_TOP_LEVEL_PER_SILO = 1

/**
 * Website Publishing Agreement version (mirror of the backend constant): signed in the setup wizard's
 * publishing step, or at package purchase when the site has not signed this version.
 */
export const POP_AUTHORITY_AGREEMENT_VERSION = '2026-08'

/** Root event carrying a confirmation message to the layout's PaToast (design pa.css .pa-toast). */
export const POP_AUTHORITY_TOAST_EVENT = 'pop-authority-toast'

/** Show the design's teal confirmation toast from any component: `showPopAuthorityToast(this, 'Saved')`. */
export function showPopAuthorityToast (vm, message) {
  if (vm && vm.$root) vm.$root.$emit(POP_AUTHORITY_TOAST_EVENT, message)
}

/** Route name of the standalone packages page (`/sites/:siteId/packages`). */
export const POP_AUTHORITY_PACKAGES_ROUTE = 'PopAuthorityPackages'

/** Standard package mixes as (top-level per month, supporting per top-level) -> key. */
export const POP_AUTHORITY_STANDARD_MIXES = { '1:3': 'starter', '2:3': 'growth', '2:5': 'pro' }

/**
 * Authority Custom hours per mix, 'tl-per': [SEO Manager hrs, publishing hrs]: the explicit values of
 * "POP Authority Packages & Costings" > Custom Combinations (design pa-data.jsx PA_CUSTOM.hoursTable).
 * Mirror of the backend table; the catalogue endpoint sends the live one as `custom.hours_table`.
 */
export const POP_AUTHORITY_CUSTOM_HOURS_TABLE = {
  '1-3': [3, 4], '1-4': [3.5, 4], '1-5': [3.5, 5], '1-6': [3.5, 5],
  '1-7': [3.5, 6], '1-8': [4, 6], '1-9': [4, 7], '1-10': [4, 7],
  '2-3': [3.5, 5], '2-4': [4, 6], '2-5': [4, 7], '2-6': [4.5, 8],
  '2-7': [4.5, 9], '2-8': [5, 10], '2-9': [5, 11], '2-10': [5.5, 12],
  '3-3': [4, 7], '3-4': [4.5, 8], '3-5': [5, 10], '3-6': [5.5, 11],
  '3-7': [5.5, 13], '3-8': [6, 14], '3-9': [6.5, 16], '3-10': [7, 17],
  '4-3': [4.5, 8], '4-4': [5, 10], '4-5': [5.5, 12], '4-6': [6, 14],
  '4-7': [6.5, 16], '4-8': [7, 18], '4-9': [7.5, 20], '4-10': [8, 22],
  '5-3': [5, 10], '5-4': [6, 12], '5-5': [6.5, 15], '5-6': [7, 17],
  '5-7': [7.5, 20], '5-8': [8.5, 22], '5-9': [9, 25], '5-10': [9.5, 27],
  '6-3': [5.5, 11], '6-4': [6.5, 14], '6-5': [7, 17], '6-6': [8, 20],
  '6-7': [8.5, 23], '6-8': [9.5, 26], '6-9': [10, 29], '6-10': [11, 32],
  '7-3': [6, 13], '7-4': [7, 16], '7-5': [8, 20], '7-6': [9, 23],
  '7-7': [9.5, 27], '7-8': [10.5, 30], '7-9': [11.5, 34], '7-10': [12.5, 37],
  '8-3': [6.5, 14], '8-4': [7.5, 18], '8-5': [8.5, 22], '8-6': [9.5, 26],
  '8-7': [10.5, 30], '8-8': [11.5, 34], '8-9': [12.5, 38], '8-10': [13.5, 42],
  '9-3': [7, 16], '9-4': [8.5, 20], '9-5': [9.5, 25], '9-6': [10.5, 29],
  '9-7': [11.5, 34], '9-8': [13, 38], '9-9': [14, 43], '9-10': [15, 47],
  '10-3': [7.5, 17], '10-4': [9, 22], '10-5': [10, 27], '10-6': [11.5, 32],
  '10-7': [12.5, 37], '10-8': [14, 42], '10-9': [15, 47], '10-10': [16.5, 52],
}

/**
 * Authority Custom fallback rates / limits (design pa-data.jsx PA_CUSTOM). The catalogue endpoint
 * returns the live values; these keep the chooser working before it loads.
 */
export const POP_AUTHORITY_CUSTOM_DEFAULTS = {
  rates: { tl: 66, sup: 48, consult_hr: 75, pub_hr: 15 },
  cost: null,
  min: { tl: 1, per: 3 },
  max: { tl: 10, per: 10 },
  defaults: { tl: 3, per: 3 },
  hours_table: POP_AUTHORITY_CUSTOM_HOURS_TABLE,
}

/** Currency (design pa$): whole dollars when the amount is whole, else two decimals ("1,231.50"). */
export function popAuthorityMoney (value) {
  const n = Math.round((Number(value) || 0) * 100) / 100
  return n.toLocaleString('en-US', { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })
}

/** Coupon.product of the percent coupons valid on any POP Authority monthly package. */
export const POP_AUTHORITY_PACKAGES_COUPON_PRODUCT = 'POP_AUTHORITY_PACKAGES'

/** 799 at 50% -> 399.5 (half-up on the cent, like Stripe); no coupon -> the price. */
export function popAuthorityDiscountedPrice (price, percentOff, amountOff) {
  const cents = Math.round((Number(price) || 0) * 100)
  // an amount coupon takes a fixed sum off, never below 0
  const amount = Math.round((Number(amountOff) || 0) * 100)
  if (amount > 0) return Math.max(cents - amount, 0) / 100
  const pct = Number(percentOff) || 0
  if (pct <= 0) return cents / 100
  return Math.round(cents * (100 - pct) / 100 + 1e-9) / 100
}

/** The price after a coupon ({ percentOff, amountOff } as the normalisers below return it); the price without one. */
export function popAuthorityCouponPrice (price, coupon) {
  if (!coupon) return Number(price) || 0
  return popAuthorityDiscountedPrice(price, coupon.percentOff, coupon.amountOff)
}

/** Is the coupon worth more than the price? Only an amount coupon can be (it is valid on every package). */
export function popAuthorityCouponExceedsPrice (price, coupon) {
  return Boolean(coupon) && Number(coupon.amountOff) > 0 && Number(coupon.amountOff) > (Number(price) || 0)
}

/**
 * { code, kind, percentOff, amountOff, duration, durationInMonths } from a validated coupon payload
 * (GET /validate-coupons): a coupon is a percentage or a fixed amount in dollars, never both.
 */
export function popAuthorityCouponFromValidated (code, coupon) {
  if (!coupon) return null
  const isAmount = coupon.couponType === 'Amount Off'
  return {
    code: String(code || '').trim().toUpperCase(),
    kind: isAmount ? 'amount' : 'percent',
    percentOff: isAmount ? 0 : Number(coupon.amountOff) || 0,
    amountOff: isAmount ? Number(coupon.amountOff) || 0 : 0,
    duration: coupon.duration || 'forever',
    durationInMonths: coupon.durationInMonths ? Number(coupon.durationInMonths) : null,
    startCycle: 1,
  }
}

/** The coupon stamped on a subscription payload (couponCode...), or null. */
export function popAuthorityCouponFromSubscription (subscription) {
  if (!subscription || !subscription.couponCode) return null
  const amountOff = (Number(subscription.couponAmountOffCents) || 0) / 100
  return {
    code: subscription.couponCode,
    kind: amountOff > 0 ? 'amount' : 'percent',
    percentOff: amountOff > 0 ? 0 : Number(subscription.couponPercentOff) || 0,
    amountOff,
    duration: subscription.couponDuration || 'forever',
    durationInMonths: subscription.couponDurationInMonths ? Number(subscription.couponDurationInMonths) : null,
    // first package month it discounts (later than 1 when it came with a package change)
    startCycle: Number(subscription.couponStartCycle) || 1,
  }
}

/** The coupon carried by a status package payload (`package.coupon`, snake_case), or null. */
export function popAuthorityCouponFromPackage (pkg) {
  const c = pkg && pkg.coupon
  if (!c || !c.code) return null
  const amountOff = Number(c.amount_off) || 0
  return {
    code: c.code,
    kind: amountOff > 0 ? 'amount' : 'percent',
    percentOff: amountOff > 0 ? 0 : Number(c.percent_off) || 0,
    amountOff,
    duration: c.duration || 'forever',
    durationInMonths: c.duration_in_months ? Number(c.duration_in_months) : null,
    startCycle: Number(c.start_cycle) || 1,
    coversCurrentCycle: Boolean(c.covers_current_cycle),
    startsNextCycle: Boolean(c.starts_next_cycle),
  }
}

/** Does the coupon discount package month `cycle`? It starts on `coupon.startCycle` (1 by default). */
export function popAuthorityCouponCoversCycle (coupon, cycle) {
  const n = Number(cycle) || 0
  if (!coupon) return false
  const start = Math.max(1, Number(coupon.startCycle) || 1)
  if (n < start) return false
  if (coupon.duration === 'once') return n === start
  if (coupon.duration === 'repeating') return n < start + (Number(coupon.durationInMonths) || 0)
  return coupon.duration === 'forever'
}

/** 'on your first invoice' | 'for 3 months' | 'every month'. */
export function popAuthorityCouponDurationLabel (coupon) {
  if (!coupon) return ''
  if (coupon.duration === 'once') return 'on your first invoice'
  if (coupon.duration === 'repeating') {
    const months = Number(coupon.durationInMonths) || 0
    return months === 1 ? 'for 1 month' : `for ${months} months`
  }
  return 'every month'
}

/** '50%' for a percent coupon, '$100' for an amount coupon. */
export function popAuthorityCouponOffLabel (coupon) {
  if (!coupon) return ''
  if (Number(coupon.amountOff) > 0) return `$${popAuthorityMoney(coupon.amountOff)}`
  return `${popAuthorityMoney(coupon.percentOff)}%`
}

/** '50% off for 3 months' | '$100 off for 3 months'. */
export function popAuthorityCouponLabel (coupon) {
  if (!coupon) return ''
  return `${popAuthorityCouponOffLabel(coupon)} off ${popAuthorityCouponDurationLabel(coupon)}`
}

/** The line after the first payment: what the following months cost with this coupon. */
export function popAuthorityCouponThenNote (price, coupon) {
  const full = popAuthorityMoney(price)
  const months = coupon ? Number(coupon.durationInMonths) || 0 : 0
  const hasCoupon = Boolean(coupon) && (Number(coupon.percentOff) > 0 || Number(coupon.amountOff) > 0)
  if (!hasCoupon || coupon.duration === 'once' || (coupon.duration === 'repeating' && months <= 1)) {
    return `Then $${full} automatically each month until you cancel.`
  }
  const reduced = popAuthorityMoney(popAuthorityCouponPrice(price, coupon))
  if (coupon.duration === 'repeating') {
    const more = months - 1
    return `Then $${reduced}/mo for ${more === 1 ? '1 more month' : `${more} more months`}, then $${full}/mo until you cancel.`
  }
  return `Then $${reduced}/mo every month until you cancel.`
}

/** Consultation and publishing hours of a custom mix: the costing sheet's explicit value per mix. */
export function popAuthorityCustomHours (tl, per, custom = POP_AUTHORITY_CUSTOM_DEFAULTS) {
  const table = (custom && custom.hours_table) || POP_AUTHORITY_CUSTOM_HOURS_TABLE
  const row = table[`${tl}-${per}`] || POP_AUTHORITY_CUSTOM_HOURS_TABLE[`${tl}-${per}`] || table['10-10']
  return { consult: Number(row[0]), publishing: Number(row[1]) }
}

/**
 * Client-side price of a custom mix (display only; the server recomputes it). Returns
 * { tl, per, sup, pages, price, lines, consultHours, publishingHours, cost, margin, matchesStandardKey }.
 * The price is exact to the cent (many mixes land on .50); a mix equal to a standard package is sold as
 * that package (`matchesStandardKey`).
 */
export function popAuthorityCustomQuote (tl, per, custom = POP_AUTHORITY_CUSTOM_DEFAULTS) {
  const cfg = custom || POP_AUTHORITY_CUSTOM_DEFAULTS
  const rates = cfg.rates || POP_AUTHORITY_CUSTOM_DEFAULTS.rates
  const t = Math.max(1, Number(tl) || 1)
  const p = Math.max(1, Number(per) || 1)
  const sup = t * p
  const pages = t + sup
  const hours = popAuthorityCustomHours(t, p, cfg)
  const lines = [
    { label: `${t} Top Level page${t > 1 ? 's' : ''}`, note: `$${rates.tl} each`, amount: t * rates.tl },
    { label: `${sup} Supporting pages`, note: `${p} per Top Level, $${rates.sup} each`, amount: sup * rates.sup },
    { label: `SEO Manager, ${hours.consult} hrs`, note: `$${rates.consult_hr}/hr`, amount: hours.consult * rates.consult_hr },
    { label: `Publishing, ${hours.publishing} hrs`, note: `$${rates.pub_hr}/hr`, amount: hours.publishing * rates.pub_hr },
  ]
  const price = Math.round(lines.reduce((sum, l) => sum + l.amount, 0) * 100) / 100
  let cost = null
  let margin = null
  if (cfg.cost) {
    // the sheet's "Cost to Produce Package": the commission equals the SEO Manager cost and is in it once
    cost = t * cfg.cost.tl + sup * cfg.cost.sup + hours.consult * cfg.cost.consult_hr + hours.publishing * cfg.cost.pub_hr
    margin = price ? (price - cost) / price : 0
  }
  return {
    tl: t,
    per: p,
    sup,
    pages,
    price,
    lines,
    consultHours: hours.consult,
    publishingHours: hours.publishing,
    cost,
    margin,
    matchesStandardKey: POP_AUTHORITY_STANDARD_MIXES[`${t}:${p}`] || null,
  }
}

/** Short package label for tags and chips: 'Authority Growth' -> 'Growth'. */
export function popAuthorityPackageShortName (pkg) {
  const name = String((pkg && pkg.name) || '')
  return name.replace('Authority ', '') || String((pkg && pkg.key) || '')
}

/** Human status of a package subscription row (GET /api/pop-authority/...). */
export function popAuthoritySubscriptionStatusLabel (sub) {
  if (!sub) return 'No package'
  if (sub.status === 'active' && sub.cancelAtPeriodEnd) return 'Ending soon'
  const labels = {
    checkout_pending: 'Checkout not completed',
    invoice_sent: 'Invoice sent',
    active: 'Active',
    past_due: 'Payment failed',
    paused: 'Paused',
    canceled: 'Ended',
  }
  return labels[sub.status] || sub.status || ''
}

export const POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES = ['active', 'past_due', 'paused']

/** Cancellation reasons offered in the cancel survey (stored on the subscription). */
export const POP_AUTHORITY_CANCEL_REASONS = [
  { key: 'too_expensive', label: 'It is too expensive right now' },
  { key: 'not_enough_results', label: 'I am not seeing enough results yet' },
  { key: 'pausing_marketing', label: 'We are pausing our marketing' },
  { key: 'switching_provider', label: 'We are moving to another provider or in-house' },
  { key: 'other', label: 'Something else' },
]

/** Effective package from a site status row (falls back to the provisional default). */
export function popAuthorityPackageFromStatus (status) {
  const fromStatus = status && status.package
  if (fromStatus && fromStatus.key) {
    return {
      ...fromStatus,
      silos_per_month: Number(fromStatus.silos_per_month) || 0,
      supporting_per_silo: Number(fromStatus.supporting_per_silo) || 0,
      top_level_per_silo: Number(fromStatus.top_level_per_silo) || POP_AUTHORITY_TOP_LEVEL_PER_SILO,
    }
  }
  const fallback = POP_AUTHORITY_PACKAGES.find((p) => p.key === POP_AUTHORITY_DEFAULT_PACKAGE_KEY)
  return { ...fallback, top_level_per_silo: POP_AUTHORITY_TOP_LEVEL_PER_SILO, provisional: true }
}

/**
 * The subscription anchor (`status.package.cycle_anchor_at`) arrives as a UTC
 * 'YYYY-MM-DD HH:MM:SS' string, which Safari refuses and Chrome reads as local time. Parse it as
 * UTC so the month name is the same everywhere.
 */
function parseUtcAnchor (anchorDate) {
  if (!anchorDate) return null
  if (anchorDate instanceof Date) return Number.isNaN(anchorDate.getTime()) ? null : anchorDate
  let text = String(anchorDate).trim().replace(' ', 'T')
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(text)) text += 'Z'
  const d = new Date(text)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Parse a UTC 'YYYY-MM-DD HH:MM:SS' API timestamp (Safari-safe). */
export function popAuthorityParseUtc (value) {
  return parseUtcAnchor(value)
}

/** "October 2026" for a campaign month, "Month N" without the subscription anchor. */
export function popAuthorityCycleMonthName (cycle, anchorDate = null) {
  const n = Number(cycle)
  if (!Number.isFinite(n) || n < 1) return ''
  const d = parseUtcAnchor(anchorDate)
  if (!d) return `Month ${n}`
  const month = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + (n - 1), 1))
  return month.toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** "October 2026" for a schedule cycle (design PA_MONTH_NAME), "Unscheduled" without one. */
export function popAuthorityCycleLabel (cycle, anchorDate = null) {
  const n = Number(cycle)
  if (!Number.isFinite(n) || n < 1) return 'Unscheduled'
  return popAuthorityCycleMonthName(n, anchorDate)
}

/** "September 2026 (cycle 1)": the "Campaign progress" line of the package widget (design OverviewTab). */
export function popAuthorityCampaignProgressLabel (cycle, anchorDate = null) {
  const n = Number(cycle)
  if (!Number.isFinite(n) || n < 1) return '—'
  return `${popAuthorityCycleMonthName(n, anchorDate)} (cycle ${n})`
}

/** Current package month from a site status row (the value lives on `status.package`). */
export function popAuthorityCurrentCycle (status) {
  const n = Number(status && status.package && status.package.current_cycle)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/**
 * Anchor of the campaign month labels before the first package payment: the first day of the
 * current month, so month 1 reads as the month production would start if the package were paid
 * today (design pa-data.jsx PA_MONTH_NAME). Once the first invoice is paid the real anchor
 * (`cycle_anchor_at`) takes over and the labels follow it.
 */
export function popAuthorityProjectedCycleAnchorAt (now = new Date()) {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString()
}

/**
 * First paid invoice date of the subscription, the anchor of every campaign month label
 * ("September 2026 (month 1)"); projected from today until the first payment.
 */
export function popAuthorityCycleAnchorAt (status) {
  return (status && status.package && status.package.cycle_anchor_at) || popAuthorityProjectedCycleAnchorAt()
}

/** True when a paid POP Authority package month produced this content order (price "Included"). */
export function popAuthorityOrderIsIncluded (order) {
  return Boolean(order && (order.included_in_package || order.includedInPackage))
}

/** "Package month 2" for order rows produced by a package month. */
export function popAuthorityPackageMonthLabel (order) {
  const n = Number((order && (order.package_cycle_number || order.packageCycleNumber)) || 0)
  return n >= 1 ? `Package month ${n}` : 'Package'
}

/** A scheduled plan needs a keyword selection, otherwise the billed month skips it. */
export function popAuthorityPlanHasSelection (silo) {
  const order = (silo && silo.content_report_order) || null
  if (!order) return false
  const top = (order.selected_top_level_keyword_ids || []).length
  const supporting = (order.selected_supporting_keyword_ids || []).length
  return top + supporting > 0
}

/**
 * What the campaign manager has to fix on a content plan before its month is produced, against the
 * package the client chose (design: a red dot on the card): [{ key, tone, text }]. Nothing while the
 * package is provisional (not chosen yet: the selection is free), for a plan already ordered, or
 * when the selection fits. `tone` is 'danger' for what blocks production (no main page, too many
 * supporting pages) and 'warning' for a plan produced with fewer pages than the package covers.
 */
export function popAuthorityPlanCapFlags (silo, packageInfo) {
  const order = (silo && silo.content_report_order) || null
  if (!packageInfo || packageInfo.provisional) return []
  if (order && ['paid', 'to_be_paid_by_invoice'].includes(order.order_status)) return []
  const top = order ? (order.selected_top_level_keyword_ids || []).length : 0
  const supporting = order ? (order.selected_supporting_keyword_ids || []).length : 0
  const cap = Number(packageInfo.supporting_per_silo) || 0
  const name = packageInfo.name || 'package'
  const flags = []
  if (top === 0) {
    flags.push({ key: 'no_main_page', tone: 'danger', text: 'No main page selected: the plan is skipped when its month is produced.' })
  }
  if (cap && supporting > cap) {
    const extra = supporting - cap
    flags.push({
      key: 'too_many_supporting',
      tone: 'danger',
      text: `Too many supporting pages: ${supporting} selected, ${name} covers ${cap}. Unselect ${extra} to proceed.`,
    })
  } else if (cap && supporting < cap) {
    const missing = cap - supporting
    flags.push({
      key: 'missing_supporting',
      tone: 'warning',
      text: supporting === 0
        ? `No supporting pages selected: ${name} covers ${cap} per plan.`
        : `${supporting} of ${cap} supporting pages selected: ${missing} more ${missing === 1 ? 'is' : 'are'} covered by ${name}.`,
    })
  }
  return flags
}

/** Why the production engine skipped a scheduled plan (admin toasts). */
export const POP_AUTHORITY_START_PRODUCTION_SKIP_REASONS = {
  no_selection: 'no keywords selected',
  already_ordered: 'already ordered',
  over_cap: 'package cap reached',
  over_keyword_cap: 'more Supporting keywords selected than the package covers',
  invalid: 'invalid order',
  error: 'error',
}

/** Months offered for scheduling: current cycle onward, rolling 12, plus the plan's current value if earlier. */
export function popAuthorityMonthOptions (currentCycle = 1, selected = null) {
  const from = Math.max(1, Number(currentCycle) || 1)
  const list = []
  for (let n = from; n < from + 12; n++) list.push(n)
  const sel = Number(selected)
  if (Number.isFinite(sel) && sel >= 1 && !list.includes(sel)) list.unshift(sel)
  return list
}

/** Content plan (silo) state for the POP Authority plans list: unscheduled | scheduled | in_progress | completed. */
export function popAuthorityPlanState (silo) {
  const order = (silo && silo.content_report_order) || null
  const ordered = Boolean(order && ['paid', 'to_be_paid_by_invoice'].includes(order.order_status))
  const fulfilment = (order && order.fulfillement_status) || ''
  if (ordered && fulfilment === 'completed') return 'completed'
  if (ordered && ['pending', 'in_progress'].includes(fulfilment)) return 'in_progress'
  if (silo && Number(silo.paScheduledCycle) >= 1) return 'scheduled'
  return 'unscheduled'
}

/**
 * A content plan whose order was invoiced or paid outside a package month. Only plans ordered
 * under White Glove before the account moved reach this state: they can never be scheduled for a
 * package month (the server refuses it and production skips them as already ordered).
 */
export function popAuthorityPlanOrderedOutsidePackage (silo) {
  const order = (silo && silo.content_report_order) || null
  return Boolean(
    order
    && ['paid', 'to_be_paid_by_invoice'].includes(order.order_status)
    && !popAuthorityOrderIsIncluded(order)
  )
}

/* ------------------------------------------------------------------------ *
 * Deliverables: review, approval and publishing (Phase 7)
 * Mirrors web/helpers/pop_authority_deliverables.py; the backend is the authority, these helpers
 * only decide what the table renders and which actions it offers.
 * ------------------------------------------------------------------------ */

/** Who publishes the delivered content (mirror of Site.publishing_mode). */
export const POP_AUTHORITY_PUBLISHING_MODE = {
  WE_PUBLISH: 'we_publish',
  SELF_PUBLISH: 'self_publish',
}

/** Per-article state; `in_progress` is synthetic (the order is still being written). */
export const POP_AUTHORITY_DELIVERABLE_STATUS = {
  IN_PROGRESS: 'in_progress',
  DELIVERED: 'delivered',
  CHANGES_REQUESTED: 'changes_requested',
  REVISED: 'revised',
  APPROVED: 'approved',
  PUBLISHED: 'published',
}

/** The client has 7 business days to review each delivered article. */
export const POP_AUTHORITY_REVIEW_WINDOW_BUSINESS_DAYS = 7

/** Row-state filters of the deliverables table (design pa-tabs.jsx ContentTab). */
export const POP_AUTHORITY_DELIVERABLE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'delivered', label: 'Delivered' },
  { id: 'completed', label: 'Completed' },
]

const DELIVERED_STATUSES = [
  POP_AUTHORITY_DELIVERABLE_STATUS.DELIVERED,
  POP_AUTHORITY_DELIVERABLE_STATUS.CHANGES_REQUESTED,
  POP_AUTHORITY_DELIVERABLE_STATUS.REVISED,
  POP_AUTHORITY_DELIVERABLE_STATUS.APPROVED,
  POP_AUTHORITY_DELIVERABLE_STATUS.PUBLISHED,
]

function selfPublish (mode) {
  return mode === POP_AUTHORITY_PUBLISHING_MODE.SELF_PUBLISH
}

/**
 * True once the article exists as a deliverable (the order was marked completed). A White Glove
 * era order has no review rows: its articles read as delivered once the order is completed.
 */
export function popAuthorityDeliverableIsDelivered (row) {
  return Boolean(row && (row.id || row.legacy) && DELIVERED_STATUSES.includes(row.status))
}

/** True while the client still has to approve or reject this article. */
export function popAuthorityDeliverableAwaitsReview (row, mode) {
  if (selfPublish(mode) || !popAuthorityDeliverableIsDelivered(row)) return false
  return [
    POP_AUTHORITY_DELIVERABLE_STATUS.DELIVERED,
    POP_AUTHORITY_DELIVERABLE_STATUS.REVISED,
  ].includes(row.status)
}

/** Filter bucket of a row: in_progress | delivered | completed (design `rowState`). */
export function popAuthorityDeliverableRowState (row, mode) {
  if (!popAuthorityDeliverableIsDelivered(row)) return 'in_progress'
  if (row.status === POP_AUTHORITY_DELIVERABLE_STATUS.PUBLISHED) return 'completed'
  if (row.status === POP_AUTHORITY_DELIVERABLE_STATUS.APPROVED) return 'completed'
  if (selfPublish(mode)) return 'completed'
  return 'delivered'
}

/** Status chip of a row: { label, tone } with tone in blue | green | orange | grey. */
export function popAuthorityDeliverableChip (row) {
  if (!popAuthorityDeliverableIsDelivered(row)) return { label: 'In progress', tone: 'blue' }
  switch (row.status) {
    case POP_AUTHORITY_DELIVERABLE_STATUS.PUBLISHED:
      return { label: 'Published', tone: 'green' }
    case POP_AUTHORITY_DELIVERABLE_STATUS.APPROVED:
      return { label: 'Approved', tone: 'green' }
    case POP_AUTHORITY_DELIVERABLE_STATUS.CHANGES_REQUESTED:
      return { label: 'In revision', tone: 'blue' }
    case POP_AUTHORITY_DELIVERABLE_STATUS.REVISED:
      return { label: 'Revised', tone: 'green' }
    default:
      return { label: 'Delivered', tone: 'green' }
  }
}

/** An order placed under White Glove before the account moved to POP Authority: shown, never acted on here. */
export function popAuthorityDeliverableIsLegacy (row) {
  return Boolean(row && row.legacy)
}

export function popAuthorityDeliverableCanApprove (row, mode) {
  if (popAuthorityDeliverableIsLegacy(row)) return false
  return popAuthorityDeliverableAwaitsReview(row, mode)
}

export function popAuthorityDeliverableCanRequestUpdates (row, mode) {
  if (popAuthorityDeliverableIsLegacy(row)) return false
  return popAuthorityDeliverableAwaitsReview(row, mode)
}

/** Self-publish projects add their own live URL until the page is recorded. */
export function popAuthorityDeliverableCanAddLiveUrl (row, mode) {
  if (popAuthorityDeliverableIsLegacy(row)) return false
  return Boolean(
    selfPublish(mode)
    && popAuthorityDeliverableIsDelivered(row)
    && row.status !== POP_AUTHORITY_DELIVERABLE_STATUS.PUBLISHED
  )
}

/** Admins record the live URL of an approved article ("Add live page"). */
export function popAuthorityDeliverableCanAdminPublish (row, mode, isAdmin) {
  if (popAuthorityDeliverableIsLegacy(row)) return false
  if (!isAdmin || !popAuthorityDeliverableIsDelivered(row)) return false
  if (selfPublish(mode)) return row.status !== POP_AUTHORITY_DELIVERABLE_STATUS.PUBLISHED
  return row.status === POP_AUTHORITY_DELIVERABLE_STATUS.APPROVED
}

export function popAuthorityDeliverableCanAdminMarkRevised (row, isAdmin) {
  if (popAuthorityDeliverableIsLegacy(row)) return false
  return Boolean(isAdmin && row && row.status === POP_AUTHORITY_DELIVERABLE_STATUS.CHANGES_REQUESTED)
}

/** Business days (Mon-Fri) from today until a deadline; 0 when it is today or past. */
export function popAuthorityBusinessDaysUntil (deadline, now = new Date()) {
  const end = popAuthorityParseUtc(deadline)
  if (!end) return null
  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const target = new Date(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
  if (target <= cursor) return 0
  let days = 0
  let guard = 0
  while (cursor < target && guard < 400) {
    cursor.setDate(cursor.getDate() + 1)
    const weekday = cursor.getDay()
    if (weekday !== 0 && weekday !== 6) days += 1
    guard += 1
  }
  return days
}

/**
 * The approval bar under the table: how many articles still need review, when they auto-approve
 * and which orders "Approve everything" has to call.
 */
export function popAuthorityReviewCountdown (rows, mode, now = new Date()) {
  const delivered = (rows || []).filter((row) => popAuthorityDeliverableIsDelivered(row))
  const pending = delivered.filter((row) => popAuthorityDeliverableAwaitsReview(row, mode))
  let deadline = null
  for (const row of pending) {
    const parsed = popAuthorityParseUtc(row.reviewDeadlineAt)
    if (parsed && (!deadline || parsed > deadline)) deadline = parsed
  }
  return {
    pending: pending.length,
    total: delivered.length,
    days: deadline ? popAuthorityBusinessDaysUntil(deadline, now) : null,
    weekdayLabel: deadline
      ? deadline.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })
      : '',
    dateLabel: deadline
      ? deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
      : '',
    orderIds: [...new Set(pending.map((row) => row.orderId))],
  }
}

/** Counts for the filter chips, keyed by filter id. */
export function popAuthorityDeliverableFilterCounts (rows, mode) {
  const counts = { all: (rows || []).length, in_progress: 0, delivered: 0, completed: 0 }
  for (const row of rows || []) {
    const state = popAuthorityDeliverableRowState(row, mode)
    counts[state] = (counts[state] || 0) + 1
  }
  return counts
}

/** Setup wizard steps (design pa-setup.jsx): 0 Setup fee, 1 Tell us about your site, 2 Publishing, 3 Connect Google. */
export const POP_AUTHORITY_SETUP_STEP = {
  FEE: 0,
  SITE_INFO: 1,
  PUBLISHING: 2,
  GOOGLE: 3,
  DONE: 4,
}

/**
 * Current wizard step for an audit row from GET /api/site-content-audits/ (no `type` key).
 * Mirrors the backend status keys: setup_ordered → 1, setup_pub_pending → 2, setup_gsc_pending → 3.
 */
export function popAuthoritySetupStep (audit) {
  if (!audit || audit.order_status !== 'paid') return POP_AUTHORITY_SETUP_STEP.FEE
  const submitted = audit.status === 'form_submitted'
  if (!submitted && !audit.siteInfoCompletedAt) return POP_AUTHORITY_SETUP_STEP.SITE_INFO
  if (!submitted) return POP_AUTHORITY_SETUP_STEP.PUBLISHING
  if (!auditHasCompletedOnboarding({ ...audit, type: 'audit_order' })) return POP_AUTHORITY_SETUP_STEP.GOOGLE
  return POP_AUTHORITY_SETUP_STEP.DONE
}

/** The per-site status row from GET /api/white-glove-orders/ `siteProjectStatuses`, or null. */
export function popAuthoritySiteStatus (siteId, siteProjectStatuses) {
  if (!siteProjectStatuses || typeof siteProjectStatuses !== 'object') return null
  return siteProjectStatuses[String(siteId)] || siteProjectStatuses[Number(siteId)] || null
}

/**
 * True while the site is in its setup phase (setup fee, site form, publishing, Google step).
 * Uses the API status when present, else infers from the paid orders like the backend does:
 * no orders at all, or a paid audit whose onboarding is not complete. Sites with only content
 * orders are not in setup.
 */
export function popAuthoritySiteInSetup (siteId, orders, siteProjectStatuses = null) {
  const status = popAuthoritySiteStatus(siteId, siteProjectStatuses)
  if (status && status.status_key) {
    return POP_AUTHORITY_SETUP_STATUS_KEYS.includes(status.status_key)
  }
  const rows = ordersForSite(orders, siteId)
  const audits = rows.filter((o) => o.type === 'audit_order')
  if (audits.length) {
    const latest = [...audits].sort(
      (a, b) => (b.order_date_timestamp || 0) - (a.order_date_timestamp || 0)
    )[0]
    return !auditHasCompletedOnboarding(latest)
  }
  return rows.length === 0
}

/* ------------------------------------------------------------------------ *
 * Booster packs (Phase 8)
 * Mirrors web/helpers/pop_authority.py (catalogue, status keys) and the row shapes of
 * helpers/pop_authority_boosters.py. Rows arrive camelCase (boosters API) or snake_case (White
 * Glove orders payload, status engine), so every helper reads both spellings.
 * ------------------------------------------------------------------------ */

/** Route name of the Boosters tab (`/sites/:siteId/boosters`, POP Authority layout only). */
export const POP_AUTHORITY_BOOSTERS_ROUTE = 'PopAuthorityBoosters'

/** Delivery is due 7 calendar days after payment. */
export const POP_AUTHORITY_BOOSTER_DELIVERY_DAYS = 7

/** Per-order booster statuses (sheet Phase 6); `boosters_needed` is the recommendation card, not a status. */
export const POP_AUTHORITY_BOOSTER_STATUS = {
  INVOICE_SENT: 'booster_invoice_sent',
  ORDERED: 'booster_ordered',
  IN_DELIVERY: 'booster_in_delivery',
  DELIVERED: 'booster_delivered',
  CANCELED: 'booster_canceled',
}
export const POP_AUTHORITY_BOOSTER_RECOMMENDED_KEY = 'boosters_needed'

export const POP_AUTHORITY_BOOSTER_OPEN_STATUS_KEYS = [
  POP_AUTHORITY_BOOSTER_STATUS.INVOICE_SENT,
  POP_AUTHORITY_BOOSTER_STATUS.ORDERED,
  POP_AUTHORITY_BOOSTER_STATUS.IN_DELIVERY,
]

/**
 * Offline catalogue mirror of web/helpers/pop_authority.py POP_AUTHORITY_BOOSTERS (design
 * pa-data.jsx PA_BOOSTERS). GET /api/pop-authority/sites/:id/boosters/ returns the live one.
 */
export const POP_AUTHORITY_BOOSTERS = [
  {
    key: 'signals',
    name: 'Local Business Signals',
    icon: 'storefront',
    unit: 'signal',
    description: 'Business citations placed across authoritative local directories to strengthen your local search presence.',
    from_label: '$245 / 10-pack',
    packs: [{ key: 'signals_10', label: '10 pack', quantity: 10, price: 245, note: '$24.50 per signal', save: 0 }],
  },
  {
    key: 'links',
    name: 'Authority Link Building',
    icon: 'link',
    unit: 'link',
    description: 'High-authority backlinks placed on relevant third-party sites, pointing directly to the pages on your website that need to rank.',
    from_label: 'From $272 / link',
    packs: [
      { key: 'links_2', label: '2 link pack', quantity: 2, price: 544, note: '$272 per link', save: 0 },
      { key: 'links_4', label: '4 link pack', quantity: 4, price: 1054, note: '', save: 34 },
      { key: 'links_8', label: '8 link pack', quantity: 8, price: 2040, note: '', save: 136 },
      { key: 'links_16', label: '16 link pack', quantity: 16, price: 3944, note: '', save: 408 },
    ],
  },
  {
    key: 'brand',
    name: 'Premium AI Brand Coverage',
    icon: 'auto_awesome',
    unit: 'article',
    description: 'Articles published on authoritative sites to generate brand mentions, getting your business cited by ChatGPT, Perplexity and AI Overviews.',
    from_label: 'From $272 / article',
    packs: [
      { key: 'brand_3', label: '3 articles', quantity: 3, price: 816, note: '$272 per article', save: 0 },
      { key: 'brand_6', label: '6 articles', quantity: 6, price: 1581, note: '', save: 51 },
      { key: 'brand_12', label: '12 articles', quantity: 12, price: 3060, note: '', save: 204 },
    ],
  },
  {
    // no longer sold: a next content audit is created for free instead (kept for the audits bought before)
    key: 'audit',
    retired: true,
    name: 'Additional Content Audit',
    icon: 'content_paste_search',
    unit: 'audit',
    description: 'A fresh content audit and plan for another round of growth. Includes 10 content silos and a 30 minute consultation.',
    from_label: '$275',
    packs: [{ key: 'audit_1', label: 'Content Audit & Plan', quantity: 1, price: 275, note: '10 content silos + 30 min call', save: 0 }],
  },
]

/** The boosters that can be bought, invoiced or recommended (a retired one only keeps its past orders). */
export function popAuthorityBoostersForSale (catalogue = POP_AUTHORITY_BOOSTERS) {
  return (catalogue || []).filter((booster) => !booster.retired)
}

/** First defined value among camelCase / snake_case spellings of a field. */
function boosterField (row, ...names) {
  if (!row) return undefined
  for (const name of names) {
    const value = row[name]
    if (value !== undefined && value !== null) return value
  }
  return undefined
}

/** A pack of the catalogue merged with its booster's name and unit, or null. */
export function popAuthorityBoosterPack (boosterKey, packKey, catalogue = POP_AUTHORITY_BOOSTERS) {
  const booster = (catalogue || []).find((b) => b.key === boosterKey)
  const pack = booster && (booster.packs || []).find((p) => p.key === packKey)
  if (!pack) return null
  return { ...pack, boosterKey: booster.key, boosterName: booster.name, unit: booster.unit }
}

/** Client-facing status of a booster row; null for drafts (`user_editing`, `in_checkout`), which never render. */
export function popAuthorityBoosterStatusKey (row) {
  const explicit = boosterField(row, 'statusKey', 'status_key')
  if (explicit) return explicit
  const orderStatus = boosterField(row, 'orderStatus', 'order_status')
  const fulfilment = boosterField(row, 'fulfillementStatus', 'fulfillement_status') || 'pending'
  if (orderStatus === 'canceled') return POP_AUTHORITY_BOOSTER_STATUS.CANCELED
  if (orderStatus === 'invoice_sent') return POP_AUTHORITY_BOOSTER_STATUS.INVOICE_SENT
  if (orderStatus === 'paid') {
    if (fulfilment === 'completed') return POP_AUTHORITY_BOOSTER_STATUS.DELIVERED
    if (fulfilment === 'in_progress') return POP_AUTHORITY_BOOSTER_STATUS.IN_DELIVERY
    return POP_AUTHORITY_BOOSTER_STATUS.ORDERED
  }
  return null
}

export function popAuthorityBoosterIsVisible (row) {
  return Boolean(popAuthorityBoosterStatusKey(row))
}

/** Invoice sent, ordered or in delivery: what the Overview "Active boosters" panel lists. */
export function popAuthorityBoosterIsOpen (row) {
  return POP_AUTHORITY_BOOSTER_OPEN_STATUS_KEYS.includes(popAuthorityBoosterStatusKey(row))
}

export function popAuthorityBoosterIsDelivered (row) {
  return popAuthorityBoosterStatusKey(row) === POP_AUTHORITY_BOOSTER_STATUS.DELIVERED
}

export function popAuthorityBoosterIsCanceled (row) {
  return popAuthorityBoosterStatusKey(row) === POP_AUTHORITY_BOOSTER_STATUS.CANCELED
}

/** Status chip of a booster row: { label, tone } with tone in orange | blue | green | grey. */
export function popAuthorityBoosterChip (row) {
  switch (popAuthorityBoosterStatusKey(row)) {
    case POP_AUTHORITY_BOOSTER_STATUS.INVOICE_SENT:
      return { label: 'Invoice sent', tone: 'orange' }
    case POP_AUTHORITY_BOOSTER_STATUS.ORDERED:
      return { label: 'Ordered', tone: 'blue' }
    case POP_AUTHORITY_BOOSTER_STATUS.IN_DELIVERY:
      return { label: 'In delivery', tone: 'blue' }
    case POP_AUTHORITY_BOOSTER_STATUS.DELIVERED:
      return { label: 'Delivered', tone: 'green' }
    case POP_AUTHORITY_BOOSTER_STATUS.CANCELED:
      return { label: 'Canceled', tone: 'grey' }
    default:
      return { label: '—', tone: 'grey' }
  }
}

export function popAuthorityBoosterName (row) {
  const name = boosterField(row, 'boosterName', 'booster_name')
  if (name) return name
  const pack = popAuthorityBoosterPack(boosterField(row, 'boosterKey', 'booster_key'), boosterField(row, 'packKey', 'pack_key'))
  return pack ? pack.boosterName : ''
}

export function popAuthorityBoosterPackLabel (row) {
  const label = boosterField(row, 'packLabel', 'pack_label')
  if (label) return label
  const pack = popAuthorityBoosterPack(boosterField(row, 'boosterKey', 'booster_key'), boosterField(row, 'packKey', 'pack_key'))
  return pack ? pack.label : ''
}

/** "Authority Link Building, 4 link pack" (either side may be missing). */
export function popAuthorityBoosterDetails (row) {
  const explicit = boosterField(row, 'label')
  if (explicit && typeof explicit === 'string') return explicit
  return [popAuthorityBoosterName(row), popAuthorityBoosterPackLabel(row)].filter(Boolean).join(', ')
}

/** Price in dollars (the boosters API sends `price`, the orders payload `price_on_checkout`). */
export function popAuthorityBoosterPrice (row) {
  return Number(boosterField(row, 'price', 'price_on_checkout')) || 0
}

export function popAuthorityBoosterReportUrl (row) {
  return boosterField(row, 'reportUrl', 'report_url') || ''
}

export function popAuthorityBoosterInvoiceUrl (row) {
  return boosterField(row, 'stripeHostedInvoiceUrl', 'stripe_hosted_invoice_url') || ''
}

/** Progress note while the booster is in delivery ("2 of 4 links placed"), '' otherwise. */
export function popAuthorityBoosterProgressNote (row) {
  if (popAuthorityBoosterStatusKey(row) !== POP_AUTHORITY_BOOSTER_STATUS.IN_DELIVERY) return ''
  return String(boosterField(row, 'progressNote', 'progress_note') || '').trim()
}

/** "Sep 21, 2026" from a UTC API timestamp, '—' when missing. */
export function popAuthorityDateLabel (value) {
  const parsed = popAuthorityParseUtc(value)
  if (!parsed) return '—'
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

/** Delivery cell: "Delivered {date}" | "Due {date}" | "After payment" | "Canceled {date}". */
export function popAuthorityBoosterDueLabel (row) {
  const key = popAuthorityBoosterStatusKey(row)
  if (key === POP_AUTHORITY_BOOSTER_STATUS.DELIVERED) {
    const at = boosterField(row, 'deliveredAt', 'delivered_at')
    return at ? `Delivered ${popAuthorityDateLabel(at)}` : 'Delivered'
  }
  if (key === POP_AUTHORITY_BOOSTER_STATUS.INVOICE_SENT) return 'After payment'
  if (key === POP_AUTHORITY_BOOSTER_STATUS.CANCELED) {
    const at = boosterField(row, 'canceledAt', 'canceled_at')
    return at ? `Canceled ${popAuthorityDateLabel(at)}` : 'Canceled'
  }
  const due = boosterField(row, 'dueAt', 'due_at')
  return due ? `Due ${popAuthorityDateLabel(due)}` : '—'
}

export function popAuthorityBoosterDeliveryPeriodLabel (row) {
  if (popAuthorityBoosterStatusKey(row) === POP_AUTHORITY_BOOSTER_STATUS.INVOICE_SENT) return 'Starts when paid'
  return `${POP_AUTHORITY_BOOSTER_DELIVERY_DAYS} days`
}

/** Paid date, else the invoice date, else creation (what the "Order date" column shows). */
export function popAuthorityBoosterOrderDate (row) {
  return boosterField(
    row, 'orderPaidAt', 'order_paid_at', 'paid_at', 'invoiceSentAt', 'invoice_sent_at',
    'createdAt', 'created_at', 'order_date',
  ) || null
}

/** Newest first (creation date, then id). */
export function popAuthorityBoosterSort (rows) {
  const ts = (row) => {
    const parsed = popAuthorityParseUtc(boosterField(row, 'createdAt', 'created_at', 'order_date'))
    if (parsed) return parsed.getTime()
    return (Number(boosterField(row, 'order_date_timestamp')) || 0) * 1000
  }
  return [...(rows || [])].sort((a, b) => (ts(b) - ts(a)) || ((Number(b.id) || 0) - (Number(a.id) || 0)))
}

/** Visible, non-canceled booster orders (stat card, note line, Deliverables chip). */
export function popAuthorityBoosterCount (rows) {
  return (rows || []).filter((row) => popAuthorityBoosterIsVisible(row) && !popAuthorityBoosterIsCanceled(row)).length
}

// --- Campaign tracking (Phase 9) ---

/** Status key of the tracking nudge card (`status.tracking_card`, a card next to the banner, not a project status). */
export const POP_AUTHORITY_ENABLE_TRACKING_KEY = 'enable_tracking'
/** `primary_cta_action` of the card when tracking turns on with one click (the data access terms were accepted). */
export const POP_AUTHORITY_TRACKING_ACTION_ENABLE = 'enable_tracking'
export const POP_AUTHORITY_TRACKING_CARD_VARIANT = {
  NOT_CONNECTED: 'not_connected',
  MONITORING_OFF: 'monitoring_off',
  EXPIRED: 'expired',
}
/** Settings tab scrolled to the Search Console card (the White Glove shell handles `?anchor=`); add `params`. */
export const POP_AUTHORITY_SETTINGS_GSC_ROUTE = { name: 'SettingsWhiteGlove', query: { anchor: 'wg-gsc' } }

/** The tracking nudge card of a site status row, or null (the backend drops it once tracking is on). */
export function popAuthorityTrackingCard (status) {
  const card = status && status.tracking_card
  if (!card || card.status_key !== POP_AUTHORITY_ENABLE_TRACKING_KEY) return null
  return card
}

/** Connected to Search Console but the monitoring consent is off (GET .../gsc `connection`): the Wins tab shows a gate. */
export function popAuthorityTrackingNeedsSetup (connection) {
  return Boolean(connection) && connection.status === 'connected' && !connection.monitoringEnabled
}

/** Tracking can be turned on with one click: the terms were accepted when Search Console was connected. */
export function popAuthorityCanEnableTrackingOneClick (connection) {
  return popAuthorityTrackingNeedsSetup(connection) && Boolean(connection.termsAcceptedAt)
}

/** A project with an open package (live, paused, past due or an unpaid first invoice) cannot be deleted. */
export function popAuthorityDeleteBlocked (pkg) {
  if (!pkg || pkg.provisional) return false
  const status = pkg.subscription_status
  return POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES.includes(status) || status === 'invoice_sent'
}
